import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { AsyncPipe, NgClass } from '@angular/common';
import { WorkspaceIndexDto } from '../../../../../../models/ts/workspace-index-dto.model';
import { WorkspaceInListDto } from '../../../../../../models/ts/workspace-in-list-dto.model';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { PopupModule} from '@progress/kendo-angular-popup';
import { ButtonModule } from '@progress/kendo-angular-buttons';
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { FilterMenuModule } from '@progress/kendo-angular-grid';
import {NgScrollbarModule} from "ngx-scrollbar";
import { TranslatePipe } from '../../../../../shared/pipes/translate/translate.pipe';
import { WorkspaceApiService } from '../../../../../api/bizzmine/workspace/workspace-api.service';
import { IconComponent } from '../../../../../shared/components/ui/icon/icon.component';
import { SidebarService } from '../../../../../shared/services/sidebar/sidebar.service';
import { ListViewComponent, ListViewModule } from '@progress/kendo-angular-listview';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DocumentEventsService } from 'src/app/shared/services/document-events/document-events.service';
import { ToggleArrowComponent } from 'src/app/shared/components/ui/toggle-arrow/toggle-arrow.component';
import { appActions } from 'src/app/store/features/app/app-actions';
import { Actions, ofType } from '@ngrx/effects';
import { PwaService } from 'src/app/core/services/pwa/pwa.service';
import { KeepOfflineService } from 'src/app/core/services/keep-offline/keep-offline.service';
import { NetworkService } from 'src/app/core/services/network/network.service';
import { networkActions } from 'src/app/store/features/network/network-actions';
import { Store } from '@ngrx/store';

@Component({
  selector: 'bizz-workspace-selector',
  templateUrl: './workspace-selector.component.html',
  styleUrls: ['./workspace-selector.component.scss'],
  imports: [
    NgClass,
    DropDownsModule,
    IconComponent,
    PopupModule,
    ButtonModule,
    AsyncPipe,
    FilterMenuModule,
    TranslatePipe,
    NgScrollbarModule,
    ListViewModule,
    FormsModule,
    ToggleArrowComponent
  ],
  standalone: true
})

export class WorkspaceSelectorComponent implements OnInit, OnDestroy, OnChanges {
  // View Children
  @ViewChild('workspaceList') public workspaceList: ListViewComponent;
  @ViewChild('inputFocus', {read: ElementRef}) private inputFocus: any;

  // Input and Output
  @Input() public showFullButton: boolean | null = true;
  @Input({required: true}) public selectedWorkspace: number;
  @Input() public selectedWorkspaceName: string;
  @Input() public isSettings: boolean;
  @Input() public isMobile = false;
  @Output() public selectedWorkspaceChange = new EventEmitter<number>();

  // Properties

  public workspaces: WorkspaceIndexDto;
  public defaultWorkspaces: WorkspaceIndexDto;
  public activeWorkspace: WorkspaceInListDto | undefined;
  // TODO: implement loading state in template
  public isLoading = true;
  public search: Subject<string> = new Subject<string>();
  private destroy: Subject<void> = new Subject<void>();
  public popupString: string;
  public expanded = this.sidebarService.getObservable();
  public dropDown = false;

  public constructor(
    private workspaceApiService: WorkspaceApiService, 
    private sidebarService: SidebarService, 
    private elementRef: ElementRef,
    private changeDetector: ChangeDetectorRef,
    private ngZone: NgZone,
    private documentEventsService: DocumentEventsService,
    private destroyRef: DestroyRef,
    private actions$: Actions,
    public pwaService: PwaService,
    private keepOfflineService: KeepOfflineService,
    public networkService: NetworkService,
    private store$: Store
  ) {
  }

  public ngOnInit(): void {
    if (this.workspaces == null) {
      this.getWorkspaces();
    } else this.isLoading = false;

    this.subscribeToSearch();

    if (this.isMobile) {
      this.popupString = "border-none bg-transparent top-0"
    } else {
      this.popupString = "border-none bg-transparent"
    }

    this.documentEventsService.documentClickEvent.
      pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
          (event: Event) => {
              if(event?.target && this.checkHandleClick(event.target)) {
                  this.ngZone.run(() => { this.onDocumentClick() });
              }
      });
    this.documentEventsService.documentKeydownEvent.
      pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
          (event: KeyboardEvent) => { 
            if(event && this.dropDown && (
              event.key === 'Escape' || 
              event.key === 'Tab' || 
              event.key === 'ArrowDown' || 
              event.key === 'Enter')) {
                this.ngZone.run(() => { this.onkeydown(event) });
            }
      });
      this.actions$.pipe(ofType(appActions.offlineModeChanged), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef)).subscribe(value => {
        if(this.pwaService.isPwa() && !value.isOnline) {
          this.setOfflineWorkspace();
        }                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                
      })

      this.actions$.pipe(ofType(networkActions.statusChanged), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef)).subscribe(value => {
        if(this.pwaService.isPwa()){
        if( value.isOnline) {
          this.getWorkspaces();
        } else{
          //not online
          if(!this.pwaService.isOnline){
            this.setOfflineWorkspace();
          }
        }
      }
      });

  }

  private setOfflineWorkspace(): void {
    this.defaultWorkspaces.Workspaces = this.defaultWorkspaces.Workspaces.filter((workspace: WorkspaceInListDto) => workspace.Offline);
    const id = this.defaultWorkspaces.Workspaces[0].ID;
    this.selectedWorkspaceChange.emit(id);
  }

  public ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedWorkspace']) {
      this.setActiveWorkspace(this.selectedWorkspace);
    }
  }

  /**
   * Listens to document click events and closes the list if the click is outside the list
   * @param event
   */
  public onDocumentClick(): void {
      this.dropDown = false;
  }

  public checkHandleClick(target: EventTarget): boolean {
    return this.dropDown && !this.elementRef.nativeElement.contains(target);
  }
  public onkeydown(event: KeyboardEvent): void { 
    if(this.dropDown) {
      if(event.key === 'Escape') {
        this.dropDown = false;
      }
      else if(this.inputFocus?.nativeElement === document.activeElement) {
        if((event.key === 'Tab' || event.key === 'ArrowDown')) {
          this.workspaceList.focus();
        } else return;
      }
      else if(event.key === 'Tab') { //focus next element on tab
        let index = this.workspaceList.activeIndex + 1;
        if(event.shiftKey)
          index = index - 2;
        if(index >= 0) {
          this.workspaceList.focus(index);
        } else this.focusInput();
      }
  
      else if(event.key === 'Enter' && this.workspaceList.activeIndex != null) {
        const activeWorkspace = this.workspaces.Workspaces[this.workspaceList.activeIndex];
        this.workspaceSelected(activeWorkspace.ID);
      }
      event.preventDefault();
    }
  }

  public toggleDropDown(event?: MouseEvent | undefined): void {
    if(this.networkService.isOnline) {
      this.dropDown = !this.dropDown;
      this.workspaces = this.defaultWorkspaces;
      this.focusInput();
    }
  }

  private focusInput(): void {
    this.changeDetector.detectChanges();
    if (this.inputFocus && this.dropDown) {
      this.inputFocus.nativeElement.focus();
    }
  }

  public onSearchEnterPressed(event: any): void {
    if(event.target['value'])
      this.workspaceSelected(this.workspaces.Workspaces[0].ID);
  }

  public workspaceSelected(id: number): void {
    this.store$.dispatch(appActions.offlineModeChanged({isOnline: true}));
    this.selectedWorkspaceChange.emit(id);
    this.setActiveWorkspace(id);
    this.toggleDropDown();
  }

  public getWorkspaces(): void {
    this.workspaceApiService.getWorkspacesResponse(true).subscribe({
      next: (response) => {
        if(response.body) {
          this.workspaces = response.body;
          this.defaultWorkspaces = response.body;
          this.setActiveWorkspace(this.selectedWorkspace);
          this.pwaService.hasOfflineWorkspace = this.workspaces.Workspaces.some((workspace: WorkspaceInListDto) => workspace.Offline);
          if(response.url){
            this.keepOfflineService.saveDataInIndexedDb(response.url, response.body);
          }
        }
      }
    });
      
  }

  public searchWorkspaces(): void {
    this.isLoading = true;
    this.workspaceApiService.searchWorkspaces(this.workspaces.TileListSearch).subscribe({
      next: (workspaces: WorkspaceIndexDto): void => {
        this.workspaces = workspaces;
      },
      error: (): void => {
        this.isLoading = false;
      },
      complete: (): void => {
        this.isLoading = false;
      },
    });
  }

  private setActiveWorkspace(id: number): void {
    this.activeWorkspace = this.workspaces?.Workspaces.find((workspace: WorkspaceInListDto) => workspace.ID === id);
  }

  /**
   * Subscribes to the search subject and updates the search property of the workspaces, then calls the API to search for workspaces
   * @private
   */
  private subscribeToSearch(): void {
    this.search.pipe(debounceTime(500), takeUntil(this.destroy)).subscribe({
      next: (search) => {
        this.workspaces.TileListSearch.Search = search;
        this.searchWorkspaces();
      },
    });
  }
}


